(function () {
  const ZONES_SELECTOR = ".product-card__media--hover-effect-image_slider";
  const ARROWS_SELECTOR =
    ".product-card__media--hover-effect-image_slider_arrows";
  const SELECTOR = `${ZONES_SELECTOR}, ${ARROWS_SELECTOR}`;
  const NAV_BUTTON_SELECTOR = ".product-card__slider-nav-button";
  const CARD_SELECTOR = "product-card, product-card-placeholder";
  const TRACK_SELECTOR = ".product-card__media-track";
  const TEMPLATE_SELECTOR = ".product-card__media-template";
  let activeMedia = null;

  function getTrack(media) {
    return media.querySelector(TRACK_SELECTOR);
  }

  function materializeSlides(media) {
    if (media.dataset.sliderReady === "true") return true;

    const track = getTrack(media);
    const template = media.querySelector(TEMPLATE_SELECTOR);

    if (!track || !template) return false;

    const firstSlide = track.firstElementChild;

    // Force eager fetch on extra slides BEFORE inserting them into the live DOM.
    // Otherwise loading="lazy" defers fetch because their layout x-position is
    // outside the viewport (translated by 100/200/300%), causing a flash on slide-in.
    const templateImgs = template.content.querySelectorAll("img");
    for (const img of templateImgs) {
      img.loading = "eager";
    }

    track.appendChild(template.content);

    if (firstSlide) {
      const clone = firstSlide.cloneNode(true);
      clone.classList.add("media--slide-clone");
      clone.setAttribute("aria-hidden", "true");
      track.appendChild(clone);
    }

    template.remove();

    // Trigger fetch + decode so the bitmap is in memory before the user reaches the slide.
    // decode() resolves once the image is loaded AND decoded; we ignore errors.
    if (typeof HTMLImageElement !== "undefined" &&
        typeof HTMLImageElement.prototype.decode === "function") {
      const liveImgs = track.querySelectorAll("img");
      for (const img of liveImgs) {
        img.decode().catch(() => {});
      }
    }

    media.dataset.sliderReady = "true";
    return true;
  }

  function clearPendingLoop(media) {
    if (!media || !media._sliderLoopHandler) return;

    const track = getTrack(media);
    if (track) {
      track.removeEventListener("transitionend", media._sliderLoopHandler);
    }
    media._sliderLoopHandler = null;
  }

  function setIndex(media, index) {
    const indexStr = String(index);
    if (media.dataset.activeIndex !== indexStr) {
      media.dataset.activeIndex = indexStr;
    }
  }

  function snapToStart(media) {
    const track = getTrack(media);
    if (!track) {
      setIndex(media, 0);
      return;
    }

    media.dataset.noTransition = "true";
    setIndex(media, 0);
    // Force reflow so the next style change re-enables transitions cleanly
    void track.offsetWidth;
    delete media.dataset.noTransition;
  }

  function loopToStart(media) {
    if (!media) return;

    const currentIndex = Number(media.dataset.activeIndex || 0);
    if (currentIndex === 0) {
      clearPendingLoop(media);
      return;
    }

    const slideCount = Number(media.dataset.sliderCount || 0);
    if (slideCount < 2) {
      setIndex(media, 0);
      return;
    }

    const track = getTrack(media);
    if (!track) {
      setIndex(media, 0);
      return;
    }

    clearPendingLoop(media);

    // Animate forward to the clone slide that sits right after the last real slide
    setIndex(media, slideCount);

    const handler = (event) => {
      if (event.target !== track || event.propertyName !== "transform") return;

      track.removeEventListener("transitionend", handler);
      media._sliderLoopHandler = null;

      // Snap back to the real first slide instantly so the loop is invisible
      snapToStart(media);
    };

    media._sliderLoopHandler = handler;
    track.addEventListener("transitionend", handler);
  }

  function loopToEnd(media, slideCount) {
    const track = getTrack(media);
    if (!track) {
      setIndex(media, slideCount - 1);
      return;
    }

    clearPendingLoop(media);

    media.dataset.noTransition = "true";
    setIndex(media, slideCount);
    void track.offsetWidth;
    delete media.dataset.noTransition;

    setIndex(media, slideCount - 1);
  }

  function stepSlide(media, delta) {
    const slideCount = Number(media.dataset.sliderCount || 0);
    if (slideCount < 2) return;

    if (!materializeSlides(media)) return;

    if (media._sliderLoopHandler) {
      clearPendingLoop(media);
      snapToStart(media);
    }

    const currentIndex = Number(media.dataset.activeIndex || 0);

    if (delta > 0) {
      if (currentIndex >= slideCount - 1) {
        loopToStart(media);
        return;
      }

      setIndex(media, currentIndex + 1);
      return;
    }

    if (currentIndex <= 0) {
      loopToEnd(media, slideCount);
      return;
    }

    setIndex(media, currentIndex - 1);
  }

  function handleNavClick(event) {
    const button = event.target.closest(NAV_BUTTON_SELECTOR);
    if (!button) return;

    const card = button.closest(CARD_SELECTOR);
    const media = card ? card.querySelector(ARROWS_SELECTOR) : null;
    if (!media) return;

    stepSlide(media, button.dataset.sliderDirection === "prev" ? -1 : 1);
  }

  function findMediaForEvent(event) {
    const direct = event.target.closest(ZONES_SELECTOR);
    if (direct) return direct;

    const card = event.target.closest(CARD_SELECTOR);
    if (!card) return null;

    return card.querySelector(ZONES_SELECTOR);
  }

  function handlePointerMove(event) {
    if (event.pointerType && event.pointerType !== "mouse") {
      return;
    }

    const media = findMediaForEvent(event);

    if (!media) {
      if (activeMedia) {
        loopToStart(activeMedia);
        activeMedia = null;
      }
      return;
    }

    const slideCount = Number(media.dataset.sliderCount || 0);
    if (slideCount < 2) return;

    const rect = media.getBoundingClientRect();
    if (rect.width === 0) return;

    if (event.clientY < rect.top || event.clientY > rect.bottom) {
      if (activeMedia) {
        loopToStart(activeMedia);
        activeMedia = null;
      }
      return;
    }

    if (activeMedia && activeMedia !== media) {
      loopToStart(activeMedia);
    }

    activeMedia = media;
    clearPendingLoop(media);

    if (!materializeSlides(media)) return;

    const zoneWidth = rect.width / slideCount;
    let index = Math.floor((event.clientX - rect.left) / zoneWidth);

    if (index < 0) index = 0;
    if (index > slideCount - 1) index = slideCount - 1;

    setIndex(media, index);
  }

  function handlePointerLeave(event) {
    const fromCard = event.target.closest(CARD_SELECTOR);
    if (!fromCard) return;

    const toCard = event.relatedTarget
      ? event.relatedTarget.closest(CARD_SELECTOR)
      : null;

    if (fromCard === toCard) return;

    const media = fromCard.querySelector(ZONES_SELECTOR);
    if (!media) return;

    loopToStart(media);

    if (activeMedia === media) {
      activeMedia = null;
    }
  }

  document.addEventListener("pointermove", handlePointerMove, {
    passive: true,
  });

  document.addEventListener("pointerout", handlePointerLeave, {
    passive: true,
  });

  document.addEventListener("click", handleNavClick);

  // Preload slides on hover-capable devices when the card approaches the viewport.
  // On touch-only devices this whole branch is skipped — no extra image requests.
  const supportsHover =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  if (supportsHover && "IntersectionObserver" in window) {
    const preloadObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          preloadObserver.unobserve(entry.target);
          materializeSlides(entry.target);
        }
      },
      { rootMargin: "300px 1000px", threshold: 0 }
    );

    function observeMedia(media) {
      if (media.dataset.sliderReady === "true") return;
      preloadObserver.observe(media);
    }

    function scanRoot(root) {
      if (!root) return;
      if (root.nodeType !== 1 && root.nodeType !== 9) return;

      if (root.matches && root.matches(SELECTOR)) {
        observeMedia(root);
        return;
      }

      const targets = root.querySelectorAll
        ? root.querySelectorAll(SELECTOR)
        : [];
      for (const media of targets) {
        observeMedia(media);
      }
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => scanRoot(document));
    } else {
      scanRoot(document);
    }

    document.addEventListener("shopify:section:load", (event) => {
      scanRoot(event.target);
    });

    // Self-healing for AJAX-inserted cards (filters, popular-products swiper rebuild, etc.)
    if ("MutationObserver" in window) {
      const mutationObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            scanRoot(node);
          }
        }
      });
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }
})();
